import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Card from '../components/common/Card';
import DataTable from '../components/common/Table';
import { useSSE } from '../hooks/useSSE';
import { usePermission } from '../hooks/usePermission';
import { toast } from '../components/common/Toast';
import { getSummary } from '../api/stats';
import { getList as getEvents } from '../api/events';
import { getList as getApartments } from '../api/apartments';
import { SpeedingEvent, StatsSummary } from '../types';
import { speedBadge } from '../utils/speedBadge';
import './Pages.css';

const CHART_COLORS = ['#4CAF50', '#F44336', '#FF9800', '#2196F3', '#9C27B0', '#00BCD4', '#FF5722', '#607D8B'];

const DashboardPage: React.FC = () => {
  const { isConnected, autoStopped, reconnect, events: wsEvents } = useSSE();
  const { canAccess } = usePermission();
  const [stats, setStats] = useState<StatsSummary | null>(null);
  const [recentEvents, setRecentEvents] = useState<SpeedingEvent[]>([]);
  const [apartmentCount, setApartmentCount] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);

  const handleReconnect = async () => {
    if (reconnecting) return;
    setReconnecting(true);
    try {
      await reconnect();
    } catch {
      toast.error('서버 응답 없음 — 잠시 후 다시 시도해주세요');
    } finally {
      setReconnecting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [s, e, a] = await Promise.all([
          getSummary(),
          getEvents({ limit: 10 }),
          getApartments({ page: 1, limit: 1 }),
        ]);
        setStats(s);
        setRecentEvents(e.items);
        setApartmentCount(a.total);
      } catch { /* ignore */ }
    };
    load();
  }, []);

  // ISS-036 — 서버가 KST 기준으로 계산한 today_count 사용. 이전에 사용하던 daily[0] 은
  // "최근 30일 중 이벤트가 있었던 가장 최근 날짜" 였기에 오늘 이벤트가 0건이면 어제/그제 값이
  // 표시되는 버그가 있었음.
  const todayCount = stats?.today_count ?? 0;

  const columns = [
    { key: 'car_number', label: '차량번호' },
    { key: 'max_speed_kmh', label: '최고속도', render: (e: SpeedingEvent) => speedBadge(e.max_speed_kmh, e.warning_speed_kmh) },
    { key: 'speed_limit_kmh', label: '제한속도', render: (e: SpeedingEvent) => `${e.speed_limit_kmh} km/h` },
    { key: 'detected_at', label: '감지시각', render: (e: SpeedingEvent) => new Date(e.detected_at).toLocaleString('ko-KR') },
  ];

  return (
    <div className="dashboard">
      <h2>대시보드</h2>
      <div className="dashboard__cards">
        <Card title="오늘 과속 건수" value={todayCount} color="danger" />
        <Card title="총 과속 건수" value={stats?.total_events ?? 0} color="danger" />
        <Card title="관리 장소" value={apartmentCount} color="primary" />
        {canAccess('realtime_status_view') && (
          <Card
            title="실시간 연결"
            value={isConnected ? '연결됨' : (autoStopped ? '끊김 (다시 연결 버튼 클릭)' : '끊김')}
            color={isConnected ? 'primary' : 'danger'}
            footer={!isConnected && (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={handleReconnect}
                disabled={reconnecting}
              >
                {reconnecting ? '연결 중...' : '다시 연결'}
              </button>
            )}
          />
        )}
      </div>

      {wsEvents.length > 0 && (
        <div className="dashboard__section">
          <h3>실시간 과속 알림</h3>
          <div className="realtime-feed">
            {wsEvents.slice(0, 5).map((e, i) => (
              <div key={i} className="realtime-feed__item">
                {speedBadge(e.max_speed_kmh, e.warning_speed_kmh)}
                <span>{e.car_number}</span>
                <span className="text-secondary">{new Date(e.detected_at).toLocaleTimeString('ko-KR')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats && (stats.daily.length > 0 || stats.by_apartment.length > 0) && (
        <div className="dashboard__section">
          <div className="stats-charts">
            <div className="stats-chart">
              <h3>일별 과속 건수 (최근 30일)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={[...stats.daily].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4CAF50" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="stats-chart">
              <h3>장소별 과속 건수</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={stats.by_apartment} dataKey="count" nameKey="apartment_name" cx="50%" cy="50%" outerRadius={80}>
                    {stats.by_apartment.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard__section">
        <h3>최근 과속 이력</h3>
        <DataTable columns={columns} data={recentEvents} />
      </div>
    </div>
  );
};

export default DashboardPage;
